import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { SiteStatsSettings, type SiteStatsSettings as Settings } from '@london-life/contracts';
import { Button } from '@london-life/ui/components/button';
import { Checkbox } from '@london-life/ui/components/checkbox';
import { Input } from '@london-life/ui/components/input';
import { Label } from '@london-life/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@london-life/ui/components/radio-group';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { StatCard } from '@london-life/ui/admin/stat-card';
import { DataTable } from '@london-life/ui/admin/data-table';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../lib/api';
import { useRequest } from '../lib/use-request';

const count = (value: number) => value.toLocaleString('en-GB');
const dayName = (day: string) =>
  new Date(`${day}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

// super admin only: the real visitor numbers, and what the homepage shows
export function StatsPage() {
  const current = useRequest(useCallback(() => api.admin.siteStats(), []));
  const [form, setForm] = useState<Settings | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (current.data) setForm(current.data.settings);
  }, [current.data]);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!form) return;
    setSaved(false);
    const parsed = SiteStatsSettings.safeParse(form);
    if (!parsed.success) return current.fail(new Error(parsed.error.issues[0]?.message));
    try {
      await api.admin.saveSiteStats(parsed.data);
      setSaved(true);
      await current.reload();
    } catch (problem) {
      current.fail(problem);
    }
  };
  const real = current.data?.real;
  const shown = form
    ? !form.showPublic
      ? 'Nothing (the numbers are hidden)'
      : form.mode === 'custom'
        ? `● ${count(form.customOnline)} online · ${count(form.customPeople)} visitors`
        : real
          ? `● ${count(Math.max(1, real.onlineNow))} online · ${count(Math.max(1, real.peopleTotal))} visitors`
          : '…'
    : '…';
  return (
    <section>
      <PageHeader
        title="Visitor stats"
        description="The real numbers, counted without cookies (one-way codes of each visitor's address; addresses are never kept; people sharing a connection count as one), and what the homepage shows."
      />
      {current.error && <ErrorNotice error={current.error} onRetry={() => void current.reload()} />}
      {real && (
        <div className="admin-panel">
          <div className="admin-stats">
            <StatCard label="Online now" value={count(real.onlineNow)} hint="seen in the last 2 minutes" />
            <StatCard
              label="Visitors all time"
              value={count(real.peopleTotal)}
              hint="different people since launch, signed up or not"
            />
            <StatCard label="Views all time" value={count(real.viewsTotal)} hint="every opening, added up" />
            <StatCard label="Views today" value={count(real.viewsToday)} hint="UK time" />
            <StatCard label="Visitors today" value={count(real.visitorsToday)} hint="unique people, UK time" />
          </div>
          <DataTable
            columns={[
              { key: 'day', header: 'Day', render: (row) => dayName(row.day) },
              { key: 'views', header: 'Views', render: (row) => count(row.views) },
              { key: 'visitors', header: 'Visitors', render: (row) => count(row.visitors) },
            ]}
            rows={real.days}
            rowKey={(row) => row.day}
            empty="No visitors yet"
          />
        </div>
      )}
      {form && (
        <form className="admin-panel admin-form" onSubmit={(event) => void submit(event)}>
          <label className="admin-checklist-item">
            <Checkbox
              checked={form.showPublic}
              onCheckedChange={(state) => setForm({ ...form, showPublic: state === true })}
            />
            <span>Show online and visitors on the homepage</span>
          </label>
          <RadioGroup
            aria-label="Which numbers"
            value={form.mode}
            onValueChange={(mode) => setForm({ ...form, mode: mode as Settings['mode'] })}
            disabled={!form.showPublic}
          >
            <label className="admin-checklist-item">
              <RadioGroupItem value="real" aria-label="Real numbers" />
              <span>Real numbers</span>
            </label>
            <label className="admin-checklist-item">
              <RadioGroupItem value="custom" aria-label="Custom numbers" />
              <span>Custom numbers</span>
            </label>
          </RadioGroup>
          {form.mode === 'custom' && form.showPublic && (
            <>
              <div className="admin-field">
                <Label htmlFor="stats-online">Online (shown)</Label>
                <Input
                  id="stats-online"
                  type="number"
                  min={0}
                  value={form.customOnline}
                  onChange={(event) =>
                    setForm({ ...form, customOnline: Math.max(0, Math.floor(Number(event.target.value) || 0)) })
                  }
                />
              </div>
              <div className="admin-field">
                <Label htmlFor="stats-people">Visitors (shown)</Label>
                <Input
                  id="stats-people"
                  type="number"
                  min={0}
                  value={form.customPeople}
                  onChange={(event) =>
                    setForm({ ...form, customPeople: Math.max(0, Math.floor(Number(event.target.value) || 0)) })
                  }
                />
              </div>
            </>
          )}
          <p className="admin-muted">
            The homepage will show: <strong>{shown}</strong>
          </p>
          <div className="admin-page-actions">
            <Button type="submit">Save</Button>
            {saved && (
              <span role="status" className="admin-muted">
                Saved. The homepage updates within a minute.
              </span>
            )}
          </div>
        </form>
      )}
    </section>
  );
}
